import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchTalents, TalentPlayer, TalentsResponse } from '../services/LeagueStatsService';
import { getLatestSeason } from '../services/MatchService';
import Loading from '../components/Loading';
import PlayerImage from '../components/PlayerImage';
import { AutoAwesome } from '@mui/icons-material';
import styles from '../styles/StatsView.module.css';

const DIVISION_TITLES = ['Ligaen', '1. Division', '2. Division', '3. Division', '4. Division', '5. Division'];

const getDivisionColor = (level: number) => {
    switch (level) {
        case 0: return '#ffd700';
        case 1: return '#c0c0c0';
        case 2: return '#cd7f32';
        case 3: return '#6c757d';
        case 4: return '#495057';
        default: return '#343a40';
    }
};

const getRankClass = (index: number) => {
    switch (index) {
        case 0: return styles.rank1;
        case 1: return styles.rank2;
        case 2: return styles.rank3;
        default: return styles.rankOther;
    }
};

const LeagueStatsTalentsPage = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<TalentsResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [season, setSeason] = useState<number>(0);
    const [availableSeasons, setAvailableSeasons] = useState<number[]>([]);
    const [maxAge, setMaxAge] = useState<number>(19);
    const [minMaps, setMinMaps] = useState<number>(3);
    const [division, setDivision] = useState<string>('all');
    const [displayCount, setDisplayCount] = useState<number>(3);

    useEffect(() => {
        const init = async () => {
            try {
                const latest = await getLatestSeason();
                setSeason(latest);
                const seasons = [];
                for (let s = latest; s >= 18; s--) seasons.push(s);
                setAvailableSeasons(seasons);
            } catch (err) {
                console.error('Error fetching latest season:', err);
                setSeason(33);
            }
        };
        init();
    }, []);

    useEffect(() => {
        if (season <= 0) return;
        const load = async () => {
            try {
                setLoading(true);
                setError(null);
                setData(await fetchTalents(season, maxAge, minMaps));
            } catch (err) {
                console.error('Error loading talents:', err);
                setError('Kunne ikke hente data');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [season, maxAge, minMaps]);

    // One section per division when showing all, else a single list for the chosen division
    const sections: { level: number; players: TalentPlayer[] }[] = [];
    if (data) {
        const players = division === 'all'
            ? data.players
            : data.players.filter(p => p.division.toLowerCase().startsWith(division));
        for (const p of players) {
            let section = sections.find(s => s.level === p.divisionLevel);
            if (!section) {
                section = { level: p.divisionLevel, players: [] };
                sections.push(section);
            }
            section.players.push(p);
        }
    }

    const renderPlayer = (player: TalentPlayer, index: number) => (
        <div key={player.steamID} className={styles.playerCard}>
            <div className={`${styles.rankBadge} ${getRankClass(index)}`}>{index + 1}</div>
            <div className={styles.playerImage}>
                <PlayerImage id={player.steamID} width={50} />
            </div>
            <div className={styles.playerInfo} style={{ flex: 2 }}>
                <Link to={`/player/${player.steamID}`} className={styles.playerName}>
                    {player.playerName}
                </Link>
                <div className={styles.teamInfo}>
                    <span>{player.teamName}</span>
                    <span
                        className={styles.divisionBadge}
                        style={{ backgroundColor: getDivisionColor(player.divisionLevel) + '33', color: getDivisionColor(player.divisionLevel) }}
                    >
                        {player.divisionName}
                    </span>
                </div>
            </div>
            <div className={styles.ratingStats}>
                <div className={styles.changeValue} style={{ color: '#f15c25', fontSize: '1.6rem' }}>
                    {player.rating.toFixed(2)}
                </div>
                <div className={styles.ratingValues}>
                    {player.age} år • {player.maps} maps • K/D {player.kd.toFixed(2)} • ADR {player.adr.toFixed(0)}
                </div>
            </div>
        </div>
    );

    if (loading) return <Loading />;

    if (error) {
        return (
            <div className="container mt-4">
                <div className="alert alert-danger" role="alert">{error}</div>
            </div>
        );
    }

    if (!data) {
        return <div className="text-center mt-5 text-white">No data available</div>;
    }

    const count = division === 'all' ? displayCount : displayCount * 3;

    return (
        <div className={styles.pageContainer}>
            <div className={styles.twitterCard}>
                <div className={styles.compactHeader}>
                    <div className={styles.headerContent}>
                        <div className={styles.titleSection}>
                            <h1 className={styles.mainTitle}>Talenter</h1>
                            <div className={styles.subtitle}>
                                De bedste spillere på {maxAge} år eller yngre
                            </div>
                        </div>
                        <div className={styles.seasonBadge}>Sæson {season}</div>
                    </div>
                </div>

                <div className={styles.singleColumnContainer}>
                    {sections.length === 0 && (
                        <div className="text-center text-white-50 py-4">
                            Ingen spillere matcher filtrene endnu
                        </div>
                    )}
                    {sections.map((section, sectionIndex) => (
                        <div key={section.level} style={{ marginTop: sectionIndex === 0 ? 0 : '20px' }}>
                            <div className={styles.columnHeader}>
                                <div
                                    className={styles.columnIcon}
                                    style={{ background: getDivisionColor(section.level) + '33' }}
                                >
                                    <AutoAwesome style={{ color: getDivisionColor(section.level) }} />
                                </div>
                                <div className={styles.columnTitle}>
                                    <h3>{DIVISION_TITLES[section.level] ?? 'Division'}</h3>
                                    <div className={styles.columnSubtitle}>
                                        {section.players.length} spillere • sorteret efter {data.ratingLabel}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.playerList}>
                                {section.players.slice(0, count).map(renderPlayer)}
                            </div>
                        </div>
                    ))}
                </div>

                <div className={styles.infoBox}>
                    <strong>Talenter:</strong> Spillere på {maxAge} år eller yngre med mindst {minMaps} maps i sæson {season},
                    sorteret efter division og derefter {data.ratingLabel}. Alderen er spillerens nuværende alder fra OverPass,
                    og spillere der har skjult deres alder er ikke med.
                </div>

                <div className={styles.footer}>
                    <div className={styles.footerText}>
                        <span className={styles.highlight}>PowerStats.dk</span> • Talent Radar • Sæson {season}
                    </div>
                </div>

                <div className={styles.filterControls}>
                    <div className={styles.filterBar}>
                        <select className={styles.filterSelect} value={season} onChange={(e) => setSeason(Number(e.target.value))}>
                            {availableSeasons.map(s => (
                                <option key={s} value={s}>Sæson {s}</option>
                            ))}
                        </select>
                        <select className={styles.filterSelect} value={division} onChange={(e) => setDivision(e.target.value)}>
                            <option value="all">Alle Divisioner</option>
                            <option value="ligaen">Ligaen</option>
                            <option value="1div">1. Division</option>
                            <option value="2div">2. Division</option>
                            <option value="3div">3. Division</option>
                            <option value="4div">4. Division</option>
                            <option value="5div">5. Division</option>
                        </select>
                        <select className={styles.filterSelect} value={maxAge} onChange={(e) => setMaxAge(Number(e.target.value))}>
                            {[16, 17, 18, 19, 20, 21, 23, 25].map(a => (
                                <option key={a} value={a}>Max {a} år</option>
                            ))}
                        </select>
                        <select className={styles.filterSelect} value={minMaps} onChange={(e) => setMinMaps(Number(e.target.value))}>
                            {[1, 3, 5, 10, 15].map(m => (
                                <option key={m} value={m}>Min. {m} maps</option>
                            ))}
                        </select>
                        <select className={styles.filterSelect} value={displayCount} onChange={(e) => setDisplayCount(Number(e.target.value))}>
                            <option value={3}>{division === 'all' ? 'Top 3 pr. division' : 'Top 9'}</option>
                            <option value={5}>{division === 'all' ? 'Top 5 pr. division' : 'Top 15'}</option>
                            <option value={10}>{division === 'all' ? 'Top 10 pr. division' : 'Top 30'}</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeagueStatsTalentsPage;
